import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

export default function Select({
  label,
  labelSuffix,
  name,
  value,
  onChange,
  options,
  placeholder,
  required = false,
  icon: Icon,
  className = "",
  labelClassName = "",
  triggerClassName = "",
  valueClassName = "",
  chevronClassName = "",
  menuClassName = "",
  optionClassName = "",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isRTL =
    typeof document !== "undefined" && document.documentElement.dir === "rtl";

  const selectedOption = options.find((opt) => opt.value === value);
  const SelectedIcon = selectedOption?.icon ?? Icon;

  const triggerClasses =
    "w-full h-10 px-4 rounded-lg flex items-center justify-between focus:outline-none transition-colors" +
    " bg-[var(--color-bg-card)] border-2 border-[var(--color-border)]" +
    " focus:border-[var(--color-primary)] focus:shadow-[0_0_8px_var(--color-shadow-primary)]";
  const iconClasses = "w-5 h-5 text-[var(--color-primary)]";
  const menuClasses =
    "absolute top-full left-0 right-0 mt-2 rounded-xl overflow-hidden z-10" +
    " bg-[var(--color-bg)] border border-[var(--color-border)]" +
    " max-h-60 overflow-y-auto";
  const optionClasses = (optValue) =>
    `w-full px-4 py-3 ${isRTL ? "text-right" : "text-left"} text-[13px] transition-colors flex items-center gap-3 ` +
    " hover:bg-[var(--color-primary-50)]" +
    ` ${value === optValue ? "text-[var(--color-primary)] bg-[var(--color-primary-50)]" : "text-[var(--color-text-primary)]"}`;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (optionValue) => {
    // Create a synthetic event to match the expected onChange signature
    onChange({ target: { name, value: optionValue } });
    setIsOpen(false);
  };

  return (
    <div className={className || "mb-4"} ref={dropdownRef}>
      {label && (
        <label
          className={`block text-sm font-medium mb-2 text-(--color-text-secondary) ${isRTL ? "text-right" : "text-left"} ${labelClassName}`}
        >
          {label}
          {labelSuffix && (
            <span
              className={`${isRTL ? "mr-1" : "ml-1"} text-xs font-normal text-(--color-text-muted)`}
            >
              {labelSuffix}
            </span>
          )}
          {required && <span className="required-asterisk" />}
        </label>
      )}

      <div className="relative">
        {/* Trigger button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`${triggerClasses} ${isRTL ? "flex-row-reverse" : ""} ${triggerClassName}`}
        >
          <div
            className={`flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""}`}
          >
            {SelectedIcon && <SelectedIcon className={iconClasses} />}
            <span
              className={`${
                value
                  ? "text-[13px] text-(--color-text-primary)"
                  : "text-(--color-primary)/70 text-[13px]"
              } ${valueClassName} ${isRTL ? "text-right" : "text-left"}`}
            >
              {selectedOption?.label || placeholder}
            </span>
          </div>
          <ChevronDown
            className={`w-5 h-5 transition-transform text-(--color-text-muted) ${isOpen ? "rotate-180" : ""} ${chevronClassName}`}
          />
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className={`${menuClasses} ${menuClassName}`}>
            {options.map((option) => {
              const OptionIcon = option.icon ?? Icon;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={`${optionClasses(option.value)} ${isRTL ? "flex-row-reverse" : ""} ${optionClassName}`}
                >
                  {OptionIcon && <OptionIcon className={iconClasses} />}
                  {option.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Hidden native select for form validation */}
      {required && (
        <select
          name={name}
          value={value}
          onChange={() => {}}
          required={required}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
